import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Container, Row, Col, ModalBody } from 'reactstrap';
import RoomListItem from './RoomListItem';
import HostBookingRoomListItem from './HostBookingRoomListItem';
import minBy from 'lodash/minBy';
import _ from 'lodash';
import find from 'lodash/find';
import WindcreekModal from 'components/WindcreekModal';
import { gaEvent } from 'utils/googleAnalytics';
import { ROOMS, BOOKING_ENGINE_ON_SCROLL } from 'utils/constants';
import { isMobileUserAgent } from 'utils/mobile';
import Button from 'components/Button';
import { HOST_BOOKING } from 'utils/routeUtils';

const StyledRoomList = styled.div`
  height: 100%;
  overflow: hidden;

  .container-fluid {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
      max-height: 28.5em;

      ${props =>
        props.hostBooking &&
        css`
          max-height: 35.5em;
        `}
    }
  }

  h4 {
    margin: 0 0 0.25em 0;
    color: ${props => props.theme.h5.color};
  }

  .subtotal {
    text-align: center;

    p {
      margin: 0;
    }

    h3 {
      margin: 0;
    }

    h3 > span {
      color: ${props => props.theme.a.color};
      font-size: 1.5em;
      vertical-align: middle;
    }

    del {
      font-weight: normal;
      font-size: 0.65em;
      margin-right: 0.3em;
    }

    a {
      color: ${props => props.theme.roomList.link.color};
      display: block;
      margin: 0.5em 0 0.7em 0;
    }
  }

  .room-description {
    font-size: 0.8em;
  }

  .room-image {
    position: relative;
    cursor: pointer;

    .image-info {
      position: absolute;
      bottom: 0.2em;
      right: 0.5em;
      color: #fff;

      span:not(.fa) {
        font-size: 0.9em;
      }

      .fa-camera {
        vertical-align: middle;
        font-size: 1.2em;
        margin-left: 0.25em;
      }
    }
  }

  .room-details {
    .btn {
      color: ${props => props.theme.roomList.link.color};
      font-weight: bold;
      text-decoration: underline;
      padding: 0;
    }
  }

  .row:nth-child(2n + 1) {
    background-color: rgba(0, 0, 0, 0.05);
  }

  *[class*='col-'] {
    padding: 0.75em;
    vertical-align: middle;
    align-items: center;
    justify-content: center;
    text-align: center;

    button {
      width: 100%;
    }
  }

  *[class*='col-']:last-child {
    border-bottom: 1px solid #dee2e6;
    padding-top: 0;
  }

  .room-list-header.row {
    display: none;
    color: #fff;
    font-weight: bold;
    text-transform: uppercase;
    background-color: ${props => props.theme.table.headerColor};
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.small}) {
    display: block;
    max-height: 31.25em;

    *[class*='col-'] {
      border-top: 1px solid #dee2e6;
      text-align: left;
      justify-content: flex-start;

      &:last-child {
        border-bottom: 0;
        padding-top: 0.75em;
      }

      button {
        width: auto;
      }
    }

    .room-list-header.row {
      min-height: 3em;
      display: flex;
      *[class*='col-'] {
        justify-content: flex-start;
      }
    }

    .subtotal {
      text-align: right;
    }
  }

  // Media query used to bridge an awkward gap between breakpoints where the select button was getting overlapped.
  @media screen and (min-width: ${props =>
      props.theme.breakpoints.small}) and (max-width: ${props =>
      props.theme.breakpoints.medium}) {
    *[class*='col-'] {
      button {
        padding: 0.5em;
        width: 100%;
      }
    }
  }
`;

const ScrollableRooms = styled.div`
  overflow-x: hidden;
  margin-left: -15px;
  margin-right: -15px;
  margin-bottom: ${props => props.theme.scrollableRooms.marginBottom};
  padding-left: 15px;
  padding-right: 15px;

  img {
    max-width: 100%;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.small}) {
    & + .no-rooms-message {
      display: block;
      margin-top: 1em;
      text-align: center;
    }
  }
`;

export class RoomList extends Component {
  state = {
    showCompModal: false,
    selectedRoom: null,
    page: null
  };

  componentDidMount() {
    const isMobile = isMobileUserAgent();

    if (isMobile) {
      window.addEventListener('message', this.handleMessageEvent);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessageEvent);
  }

  handleMessageEvent = event => {
    if (event.data.type === BOOKING_ENGINE_ON_SCROLL) {
      this.setState({
        page: {
          scrollY: event.data.payload.scrollY,
          height:
            window.screen.availHeight - 2 * event.data.payload.headerOffset
        }
      });
    }
  };

  getRoomWithFlags = room => {
    const { property } = this.props;
    const roomTypeCode = room.roomType.roomTypeCodes.roomTypeCode;

    const roomInfo = ROOMS[property][roomTypeCode];

    gaEvent({
      category: 'Hotel Booking',
      action: 'Selected Room Type',
      label: room.roomType.roomTypeDescription
    });

    return Object.assign({}, room, {
      smoking: roomInfo.smoking,
      accessible: roomInfo.accessible
    });
  };

  onSelect = room => {
    const { onSelect } = this.props;

    onSelect(this.getRoomWithFlags(room));
  };

  toggleCompModal = room => {
    this.setState({
      showCompModal: !this.state.showCompModal,
      selectedRoom: room
    });
  };

  dismissCompModal = () => {
    const { onSelect } = this.props;
    const { selectedRoom } = this.state;

    onSelect(this.getRoomWithFlags(selectedRoom));
    this.toggleCompModal();
  };

  render() {
    const {
      rooms,
      rewards,
      bookingSource,
      property,
      error,
      isWaitListBooking,
      waitListBookingReason,
      isHostBooking
    } = this.props;
    const { showCompModal, page } = this.state;
    const isMobile = isMobileUserAgent();

    // Calculate min room rate, rack rate, and add isDiscounted flag,
    // then order by the total and room type description.
    const orderedRooms = _.chain(rooms)
      .map(room => {
        const minRoomRate = minBy(
          room.roomRates,
          roomRate => roomRate.total.amount
        );
        const rackRoomRate = find(
          room.roomRates,
          roomRate =>
            roomRate.ratePlanCode === '10_FIT' && roomRate.total.amount > 0
        );
        const isDiscounted =
          rackRoomRate &&
          Math.round(rackRoomRate.total.amount / 100) !==
            Math.round(minRoomRate.total.amount / 100);

        return { ...room, minRoomRate, rackRoomRate, isDiscounted };
      })
      .orderBy(['minRoomRate.total.amount', 'roomType.roomTypeDescription'])
      .value();

    const propertyRestricted = error && error === 'PROPERTY_RESTRICTED';
    const noRooms = !rooms || (rooms && rooms.length === 0 && !error);

    return (
      <StyledRoomList hostBooking={bookingSource === HOST_BOOKING}>
        <Container fluid>
          <Row className="room-list-header">
            <Col xs={9}>Room Preference</Col>
            <Col xs={3} className="text-right">
              Price
            </Col>
          </Row>
          <ScrollableRooms>
            {orderedRooms.map((room, i) => {
              const tax = room.minRoomRate.expectedCharges.totalTaxesAndFees;
              const subTotal = Math.max(0, room.minRoomRate.total.amount - tax);

              // Set original prices based on the rack rates, if there is a discount.
              const originalSubtotal = room.isDiscounted
                ? room.rackRoomRate.expectedCharges.totalRoomAndRatePackages
                : null;
              const showCompMessage =
                room.minRoomRate.eligibleForComp &&
                !!rewards?.length &&
                !isHostBooking;

              return isHostBooking && isMobile ? (
                <HostBookingRoomListItem
                  key={i}
                  index={i}
                  room={room}
                  property={property}
                  subTotal={subTotal}
                  originalSubtotal={originalSubtotal}
                  rewards={rewards}
                  onSelect={
                    showCompMessage ? this.toggleCompModal : this.onSelect
                  }
                  isWaitListBooking={isWaitListBooking}
                  waitListBookingReason={waitListBookingReason}
                />
              ) : (
                <RoomListItem
                  key={i}
                  index={i}
                  room={room}
                  property={property}
                  subTotal={subTotal}
                  originalSubtotal={originalSubtotal}
                  rewards={rewards}
                  onSelect={
                    showCompMessage ? this.toggleCompModal : this.onSelect
                  }
                  page={page}
                  isWaitListBooking={isWaitListBooking}
                  waitListBookingReason={waitListBookingReason}
                />
              );
            })}
          </ScrollableRooms>
          {(noRooms || propertyRestricted) && (
            <p className="no-rooms-message">
              Sorry, there are no available rooms that fit that criteria.
            </p>
          )}
          {error && !propertyRestricted && (
            <p className="no-rooms-message">{error}</p>
          )}
        </Container>

        <WindcreekModal
          isOpen={showCompModal}
          backdrop={false}
          toggle={this.toggleCompModal}
          page={page}
          autoFocus
          centered
        >
          <ModalBody>
            <h1>COMP Bookings</h1>
            <p>
              For COMP hotel bookings, ROOM TYPE PREFERENCE is not guaranteed
              until check in. Your selection will be noted in your booking.
            </p>
            <Button secondary onClick={this.dismissCompModal}>
              I UNDERSTAND
            </Button>
          </ModalBody>
        </WindcreekModal>
      </StyledRoomList>
    );
  }
}

RoomList.propTypes = {
  rooms: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  nights: PropTypes.number,
  property: PropTypes.string,
  rewards: PropTypes.array,
  bookingSource: PropTypes.string,
  isWaitListBooking: PropTypes.bool,
  error: PropTypes.string,
  waitListBookingReason: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }),
  isHostBooking: PropTypes.bool
};

export default RoomList;
